import { useMemo } from "react";

import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getCoverageMatrix } from "@/api/kpi";

/**
 * Day × 4-hour-band coverage grid.
 *
 * The monthly totals answer "how much is missing"; this answers "when".
 * One row per day, one cell per band, coloured by fill ratio — a night shift
 * that is chronically half-staffed shows up as a vertical red stripe.
 */
function ratioColor(
  ratio: number | null,
  mode: "light" | "dark",
): string {
  if (ratio === null) return mode === "dark" ? "#2a2a2a" : "#f2f2f2"; // closed
  if (ratio >= 1) return mode === "dark" ? "#2e7d32" : "#66bb6a";
  if (ratio >= 0.75) return mode === "dark" ? "#9e7d0a" : "#ffd54f";
  if (ratio >= 0.5) return mode === "dark" ? "#b25e00" : "#ffab40";
  if (ratio > 0) return mode === "dark" ? "#b71c1c" : "#ef5350";
  return mode === "dark" ? "#7f1d1d" : "#e53935"; // nothing at all
}

export function CoverageMatrixCard({
  organizationId,
  year,
  month,
}: {
  organizationId: string;
  year: number;
  month: number;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode;

  const matrix = useQuery({
    queryKey: ["coverage-matrix", organizationId, year, month],
    queryFn: () => getCoverageMatrix(organizationId, year, month),
    enabled: Boolean(organizationId),
  });

  const legend = useMemo(
    () => [
      { label: "100 %", ratio: 1 },
      { label: "≥ 75 %", ratio: 0.8 },
      { label: "≥ 50 %", ratio: 0.6 },
      { label: "< 50 %", ratio: 0.2 },
      { label: "0 %", ratio: 0 },
      { label: t("dashboard.matrixClosed"), ratio: null },
    ],
    [t],
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t("dashboard.matrixTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("dashboard.matrixHint")}
        </Typography>

        {matrix.isLoading && <CircularProgress size={20} />}
        {matrix.isError && (
          <Alert severity="error">{(matrix.error as Error).message}</Alert>
        )}

        {matrix.data && (
          <>
            {matrix.data.worst_bands.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("dashboard.matrixWorst", {
                  bands: matrix.data.worst_bands.join(", "),
                })}
              </Alert>
            )}

            <Box sx={{ overflowX: "auto" }}>
              <Box
                component="table"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "2px",
                  fontSize: 11,
                  "& th": { fontWeight: 600, color: "text.secondary", px: 0.5 },
                }}
              >
                <thead>
                  <tr>
                    <th />
                    {matrix.data.band_labels.map((label) => (
                      <th key={label} style={{ whiteSpace: "nowrap" }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.data.rows.map((row) => (
                    <tr key={row.date}>
                      <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {row.date.slice(8, 10)}
                      </th>
                      {row.cells.map((cell) => (
                        <Box
                          component="td"
                          key={cell.band}
                          sx={{ p: 0, lineHeight: 0 }}
                        >
                          <Tooltip
                            title={
                              cell.ratio === null
                                ? t("dashboard.matrixClosed")
                                : `${row.date} · ${matrix.data!.band_labels[cell.band]} — ${cell.assigned.toFixed(1)} / ${cell.required.toFixed(1)} h`
                            }
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 18,
                                borderRadius: 0.5,
                                bgcolor: ratioColor(cell.ratio, mode),
                              }}
                            />
                          </Tooltip>
                        </Box>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{ mt: 2 }}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
            >
              {legend.map((l) => (
                <Stack key={l.label} direction="row" spacing={0.5} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 0.5,
                      bgcolor: ratioColor(l.ratio, mode),
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {l.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
